/**
 * Log a demand, into the project you are looking at.
 *
 * A QUOTE REQUEST IS A SPECIES WITH A SATELLITE, which is why this dialog still exists. It became
 * the nineteenth species on 2026-10-27 -- an Object with a number, an audit trail and a project
 * membership like its four siblings' -- but who asked, what arrived and what is wanted live on
 * raw.QUOTE_REQUEST, keyed on the revision, and `Create Quote Request` writes the record AND the
 * row. CreateObject writes only the record, so it would file a request that says nothing. Before
 * the conversion this was the only way at all: a request was "a plain row" with no classification.
 *
 * AND NOTHING ELSE COULD EITHER. Phase 6 shipped the service, its door and its public API, and no
 * screen: before this, `Create Quote Request` was reachable by an integrator holding a credential
 * and by nobody sitting in the product. There is no quote request list page, so this dialog is not
 * a convenience on top of one -- it is the only way a person can record that somebody asked for a
 * price.
 *
 * THE SAVE RULE IS THE SERVER'S, SPELT THE SAME WAY. `_subject_matter` refuses a request that names
 * no item, no product and no subject, because "a request with none of those cannot be acted on" --
 * so the button is disabled on exactly that condition rather than letting somebody fill in a
 * requester and be refused. The server still decides; this only avoids a pointless round trip.
 *
 * WHAT IS DELIBERATELY NOT HERE: the item picker. The service accepts an ItemObjectUSID and will
 * resolve the item's name from it, which is the third way to say what was asked for -- but a
 * lookup against the item list is a component of its own, and a demand that names a product in
 * words is the ordinary case for something keyed in off a phone call or an email.
 */
import React, {useEffect, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField,
        Typography} from "@mui/material";

import {Grey7} from "../../theme/styles";

type Props = {
    open: boolean;
    onClose: () => void;
    /** Create it, then hand it back so the caller can file it. */
    onCreate: (body: Record<string, any>) => void;
    busy?: boolean;
    /** The project's customer, filed with the request so the demand knows who asked. */
    customerObjectUSID?: string | null;
    /**
     * THE PROJECT'S PRODUCTS, so the demand can be filed under one and sit in that product's
     * lane. A Product is a development subject and not an Item, which is why this is the
     * folder's products and not the item catalogue; the service refuses an Item here.
     */
    products?: {MemberUSID: string; Name?: string | null; ObjectID?: string | null}[];
    /** The lane the dialog was opened from, preselected. Null opens it outside any pipeline. */
    productObjectUSID?: string | null;
};

export default function CreateQuoteRequest({open, onClose, onCreate, busy, customerObjectUSID,
                                            products, productObjectUSID}: Props) {
    const [product_, setProduct_] = useState<string>("");
    const [subject, setSubject] = useState("");
    const [product, setProduct] = useState("");
    const [quantity, setQuantity] = useState("");
    const [unit, setUnit] = useState("");
    const [requester, setRequester] = useState("");
    const [email, setEmail] = useState("");
    const [notes, setNotes] = useState("");

    // CLEARED ON EACH OPEN, not on close: a dialog that keeps the last request's subject would
    // offer it as the next one's, and the one time that is wrong is the time nobody notices.
    useEffect(() => {
        if (!open) return;
        setSubject(""); setProduct(""); setQuantity(""); setUnit("");
        setRequester(""); setEmail(""); setNotes("");
        // THE LANE IT WAS OPENED FROM, if any. Re-read on each open so a dialog opened from one
        // product and then from another does not carry the first.
        setProduct_(productObjectUSID ?? "");
    }, [open, productObjectUSID]);

    const saysWhat = Boolean(subject.trim() || product.trim());
    // ABOVE ZERO OR NOT AT ALL, which is what `_quantity` enforces. An empty box is a quantity
    // nobody gave; a zero is a number somebody typed and the server refuses it.
    const quantityOk = quantity.trim() === "" || Number(quantity) > 0;

    const save = () => {
        const body: Record<string, any> = {};
        if (subject.trim()) body.Subject = subject.trim();
        if (product.trim()) body.RequestedProduct = product.trim();
        if (quantity.trim()) body.Quantity = quantity.trim();
        if (unit.trim()) body.UnitOfMeasure = unit.trim();
        if (requester.trim()) body.RequesterName = requester.trim();
        if (email.trim()) body.RequesterEmail = email.trim();
        if (notes.trim()) body.Notes = notes.trim();
        // THE PROJECT'S CUSTOMER, and only when it has one. A project is often opened before
        // anybody has filed the customer as a record, and sending an empty string would look like
        // a deliberate blank rather than an absent fact.
        if (customerObjectUSID) body.CustomerObjectUSID = customerObjectUSID;
        if (product_) body.ProductObjectUSID = product_;
        onCreate(body);
    };

    return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{fontSize: 16}}>Log a quote request</DialogTitle>
        <DialogContent sx={{display: "flex", flexDirection: "column", gap: "16px",
                            paddingTop: "8px !important"}}>
            <Typography sx={{fontSize: 12.5, color: Grey7, lineHeight: 1.6}}>
                A demand somebody made — an email, a phone call, a portal submission. Say what was
                asked for in the subject line it arrived with, or describe the product.
            </Typography>
            {products && products.length > 0 && (
                <TextField select size="small" label="Product pipeline" value={product_}
                           helperText="Which of this project's products the demand is for. Leave it blank for a demand that belongs to no product yet."
                           onChange={event => setProduct_(event.target.value)}>
                    <MenuItem value="">
                        <em>None — outside any product pipeline</em>
                    </MenuItem>
                    {products.map(one => (
                        <MenuItem key={one.MemberUSID} value={one.MemberUSID}>
                            {one.Name ?? one.ObjectID ?? one.MemberUSID}
                        </MenuItem>
                    ))}
                </TextField>
            )}
            <TextField autoFocus size="small" label="Subject" value={subject}
                       onChange={event => setSubject(event.target.value)}/>
            <TextField size="small" label="Requested product" value={product}
                       onChange={event => setProduct(event.target.value)}/>
            <Box sx={{display: "flex", gap: "16px"}}>
                <TextField size="small" label="Quantity" value={quantity} sx={{flex: 1}}
                           error={!quantityOk}
                           helperText={quantityOk ? " " : "A requested quantity must be above zero."}
                           onChange={event => setQuantity(event.target.value)}/>
                <TextField size="small" label="Unit" value={unit} sx={{flex: 1}}
                           helperText=" "
                           onChange={event => setUnit(event.target.value)}/>
            </Box>
            <Box sx={{display: "flex", gap: "16px"}}>
                <TextField size="small" label="Requester" value={requester} sx={{flex: 1}}
                           onChange={event => setRequester(event.target.value)}/>
                <TextField size="small" label="Their email" value={email} sx={{flex: 1}}
                           onChange={event => setEmail(event.target.value)}/>
            </Box>
            <TextField size="small" label="Notes" value={notes} multiline minRows={2}
                       onChange={event => setNotes(event.target.value)}/>
            {!saysWhat && (
                <Typography sx={{fontSize: 12, color: Grey7, lineHeight: 1.6}}>
                    A request must say what was asked for: give the subject line it arrived with, or
                    describe the product.
                </Typography>
            )}
        </DialogContent>
        <DialogActions>
            <Button size="small" onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
            <Button size="small" variant="contained" disableElevation
                    disabled={!saysWhat || !quantityOk || Boolean(busy)}
                    sx={{textTransform: "none"}} onClick={save}>
                Log it
            </Button>
        </DialogActions>
    </Dialog>;
}
